import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

/**
 * Reusable sortable column header. Click toggles asc → desc → off.
 *
 * Props:
 *   column       string  — unique key for this column (matches accessor)
 *   sortKey      string  — current sort column (from parent state)
 *   sortDir      "asc" | "desc"
 *   onSort(col)  fn      — parent handler: cycles asc/desc/off
 *   align        "left" | "right" | "center"
 *   className    extra tailwind classes (positioning, bg, padding)
 *   children     label content
 */
export default function SortableTh({
  column,
  sortKey,
  sortDir,
  onSort,
  align = "left",
  className = "",
  children,
  testId,
}) {
  const active = sortKey === column;
  const Icon = !active ? CaretUpDown : sortDir === "asc" ? CaretUp : CaretDown;
  const alignCls = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(column)}
        data-testid={testId || `sort-${column}`}
        data-sort-active={active ? sortDir : "off"}
        className={`inline-flex items-center gap-1 w-full select-none ${alignCls} ${active ? "text-slate-900" : "text-inherit hover:text-slate-800"}`}
        title={active ? `Sorted ${sortDir}. Click to ${sortDir === "asc" ? "descend" : "clear"}.` : "Sort"}
      >
        <span>{children}</span>
        <Icon size={10} weight={active ? "bold" : "regular"} className={active ? "opacity-100" : "opacity-40"} />
      </button>
    </th>
  );
}

/** Cycle helper for parent state: click a column → asc → desc → (off) */
export function nextSort(currentKey, currentDir, clickedKey) {
  if (currentKey !== clickedKey) return { key: clickedKey, dir: "asc" };
  if (currentDir === "asc") return { key: clickedKey, dir: "desc" };
  return { key: "", dir: "asc" }; // third click clears
}

/** Generic comparator: handles strings, numbers, and ISO dates; nulls sort last. */
export function compareBy(a, b, accessor) {
  const va = typeof accessor === "function" ? accessor(a) : a?.[accessor];
  const vb = typeof accessor === "function" ? accessor(b) : b?.[accessor];
  const na = va == null || va === "";
  const nb = vb == null || vb === "";
  if (na && nb) return 0;
  if (na) return 1;
  if (nb) return -1;
  if (typeof va === "number" && typeof vb === "number") return va - vb;
  // localeCompare with numeric:true handles "US5" < "US10" < "US100" correctly
  return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
}
